import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Phone, Mail, MapPin, Clock, Facebook, Instagram, Linkedin } from 'lucide-react';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    // FormSubmit will handle everything
    setIsSubmitting(true);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-slate-900 py-20">
        <div className="container mx-auto px-6">
          <h1 className="text-5xl md:text-6xl font-bold text-white text-center mb-6">
            Get in <span className="text-[#FF9900]">Touch</span>
          </h1>
          <p className="text-xl text-gray-200 text-center max-w-3xl mx-auto">
            Ready to elevate your Amazon presence? Let's discuss how we can help your brand grow.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Contact Information */}
            <div>
              <h2 className="text-4xl font-bold text-slate-900 mb-8">
                Contact <span className="text-[#FF9900]">Information</span>
              </h2>
              
              <div className="space-y-6 mb-12">
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-[#FF9900] p-3 rounded-lg">
                        <MapPin className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900 mb-1">Address</h3>
                        <p className="text-gray-600">
                          Delaware, USA
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-[#FF9900] p-3 rounded-lg">
                        <Phone className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900 mb-1">Phone</h3>
                        <a href="tel:+17046845721" className="text-gray-600 hover:text-[#FF9900] transition-colors">
                          +1 704-684-5721
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-[#FF9900] p-3 rounded-lg">
                        <Mail className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900 mb-1">Email</h3>
                        <a href="mailto:contact@eymellc.com" className="text-gray-600 hover:text-[#FF9900] transition-colors">
                          contact@eymellc.com
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-[#FF9900] p-3 rounded-lg">
                        <Clock className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900 mb-1">Business Hours</h3>
                        <p className="text-gray-600">
                          Monday – Friday: 9:00 AM – 5:00 PM<br />
                          Saturday: Closed<br />
                          Sunday: Closed
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Social Media */}
              <div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Let's Be Social</h3>
                <div className="flex gap-4">
                  <a 
                    href="https://instagram.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="bg-slate-100 hover:bg-[#FF9900] p-3 rounded-lg transition-all duration-300 group"
                  >
                    <Instagram className="w-6 h-6 text-slate-900 group-hover:text-white" />
                  </a>
                  <a 
                    href="https://facebook.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="bg-slate-100 hover:bg-[#FF9900] p-3 rounded-lg transition-all duration-300 group"
                  >
                    <Facebook className="w-6 h-6 text-slate-900 group-hover:text-white" />
                  </a>
                  <a 
                    href="https://linkedin.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="bg-slate-100 hover:bg-[#FF9900] p-3 rounded-lg transition-all duration-300 group"
                  >
                    <Linkedin className="w-6 h-6 text-slate-900 group-hover:text-white" />
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <Card className="border-0 shadow-xl">
                <CardContent className="p-8">
                  <h2 className="text-3xl font-bold text-slate-900 mb-6">
                    Send Us a <span className="text-[#FF9900]">Message</span>
                  </h2>
                  <form 
                    onSubmit={handleSubmit} 
                    className="space-y-6"
                    action="https://formsubmit.co/contact@eymellc.com" 
                    method="POST"
                  >
                    {/* FormSubmit Hidden Configuration Fields */}
                    <input type="hidden" name="_next" value="https://eymellc.com/#/contact?success=true" />
                    <input type="hidden" name="_subject" value="Yeni İletişim Formu - EYME LLC Website" />
                    <input type="hidden" name="_captcha" value="false" />
                    <input type="hidden" name="_template" value="table" />
                    <input type="hidden" name="_autoresponse" value="Mesajınız için teşekkür ederiz! En kısa sürede size geri dönüş yapacağız. - EYME LLC" />
                    <input type="text" name="_honey" style={{display: 'none'}} tabIndex="-1" autoComplete="off" />
                    
                    <div>
                      <Label htmlFor="name" className="text-slate-900 font-semibold mb-2 block">
                        Full Name *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full"
                        placeholder="John Doe"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email" className="text-slate-900 font-semibold mb-2 block">
                        Email Address *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full"
                        placeholder="john@example.com"
                      />
                    </div>

                    <div>
                      <Label htmlFor="company" className="text-slate-900 font-semibold mb-2 block">
                        Company
                      </Label>
                      <Input
                        id="company"
                        name="company"
                        type="text"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full"
                        placeholder="Your Company Name"
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone" className="text-slate-900 font-semibold mb-2 block">
                        Phone Number
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-slate-900 font-semibold mb-2 block">
                        Message *
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        value={formData.message}
                        onChange={handleChange}
                        className="w-full min-h-32"
                        placeholder="Tell us about your business and how we can help..."
                      />
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-[#FF9900] hover:bg-[#e68a00] text-white py-6 text-lg font-semibold"
                    >
                      Send Message
                    </Button>

                    <p className="text-sm text-gray-500 text-center mt-4">
                      Bu form FormSubmit.co ile güvenli bir şekilde korunmaktadır.
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-slate-900 mb-8 text-center">
            Find Us on the <span className="text-[#FF9900]">Map</span>
          </h2>
          <div className="max-w-5xl mx-auto">
            <div className="aspect-video rounded-xl overflow-hidden shadow-xl">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3266.6158789433857!2d-80.66847492380524!3d35.07650957280473!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88541f0a7c9e1c1f%3A0x8e5e5e5e5e5e5e5e!2s110%20Associates%20Ln%2C%20Indian%20Trail%2C%20NC%2028079!5e0!3m2!1sen!2sus!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="EYME LLC Location"
              ></iframe>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
